import React, { useEffect, useRef, useState } from "react";
import { Card, Form, Input, Select, Button, Table, Typography, Space, Tag, DatePicker, message, Tooltip, Empty } from "antd";
import { SendOutlined, HistoryOutlined, PhoneOutlined, FilterFilled, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { addDriverCallRequest, clearDriverCallStatus } from "../../redux/actions/driverCallsAction";
import { DriverCall } from "../../redux/reducers/driverCallsReducer";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { RangePicker } = DatePicker;

const PURPLE = "rgb(136, 66, 153)";

const REASONS = ["Amount change", "Login Request", "Low revenue", "Subscription", "Others"];

const formColStyle: React.CSSProperties = {
    flex: "0 0 28.2%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
};
const tableColStyle: React.CSSProperties = {
    flex: "1 1 auto",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
};
const fitCardStyle: React.CSSProperties = {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};
const historyBodyStyle: React.CSSProperties = {
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    padding: 0,
};

const AddDrivers = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const [messageApi, contextHolder] = message.useMessage();
    const messageApiRef = useRef(messageApi);
    useEffect(() => {
        messageApiRef.current = messageApi;
    }, [messageApi]);

    const { user } = useSelector((state: any) => state.auth);
    const { adding, addError, addSuccess } = useSelector((state: any) => state.driverCalls);

    const onFinish = (values: any) => {
        dispatch(addDriverCallRequest({
            cabId: values.cabId?.trim(),
            reason: values.reason,
            remarks: values.remarks || "",
            type: values.type,
            updateBy: user?.username || "Admin",
        }));
    };

    useEffect(() => {
        if (addSuccess) {
            messageApiRef.current.success("Driver call saved successfully!");
            form.resetFields();
            dispatch(clearDriverCallStatus());
        }
    }, [addSuccess, dispatch, form]);

    useEffect(() => {
        return () => {
            dispatch(clearDriverCallStatus());
        };
    }, [dispatch]);

    useEffect(() => {
        if (addError) {
            messageApiRef.current.error(addError);
        }
    }, [addError]);

    const rows: DriverCall[] = [];

    const incomingCount = rows.filter((item: DriverCall) => item.type === "incoming").length;
    const outgoingCount = rows.filter((item: DriverCall) => item.type === "outgoing").length;

    const historyColumns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
            render: (text: string) => (
                <Text style={{ color: "#666", fontWeight: 500 }}>{text}</Text>
            ),
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            width: 120,
            render: (text: string) => <Text>{text?.split(" ")[0]}</Text>,
        },
        {
            title: "Time",
            dataIndex: "date",
            key: "time",
            width: 110,
            render: (text: string) => (
                <Text style={{ fontVariantNumeric: "tabular-nums" }}>
                    {text?.split(" ").slice(1).join(" ")}
                </Text>
            ),
        },
        {
            title: "Cab ID",
            dataIndex: "cabId",
            key: "cabId",
            width: 120,
            render: (text: string) => (
                <Tag color="purple" style={{ borderRadius: 6, fontWeight: 600, padding: "2px 8px" }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 140,
            filters: [
                { text: "Incoming", value: "incoming" },
                { text: "Outgoing", value: "outgoing" },
            ],
            onFilter: (value: any, record: any) => record.type === value,
            filterIcon: (filtered: boolean) => (
                <FilterFilled style={{ color: filtered ? "#ffd666" : "#fff", fontSize: 14 }} />
            ),
            render: (text: string) => (
                <Tag
                    color={text === "incoming" ? "blue" : "green"}
                    style={{ borderRadius: 6, fontWeight: 500, padding: "2px 8px" }}
                >
                    {text === "incoming" ? "Incoming 📥" : "Outgoing 📤"}
                </Tag>
            ),
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: 160,
            render: (text: string) => (
                <Text strong style={{ color: "#444", textTransform: "capitalize" }}>
                    {text?.replace(/_/g, " ")}
                </Text>
            ),
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            render: (text: string) =>
                text ? (
                    <Tooltip title={text.length > 60 ? text : ""}>
                        <Text style={{ color: "#555" }} ellipsis>{text}</Text>
                    </Tooltip>
                ) : (
                    <Text style={{ color: "#bbb" }}>—</Text>
                ),
        },
        {
            title: "Updated By",
            dataIndex: "updateBy",
            key: "updateBy",
            width: 140,
            render: (text: string) => (
                <Tag color="geekblue" style={{ borderRadius: 6, fontWeight: 500, padding: "2px 8px" }}>
                    {text}
                </Tag>
            ),
        },
    ];

    return (
        <div className="fit-page">
            {contextHolder}
            <div className="drv-layout">
                <div style={formColStyle}>
                    <Card
                        title={
                            <Space size={10} style={{ color: "#fff", padding: "4px 0" }}>
                                <PhoneOutlined style={{ fontSize: 20 }} />
                                <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.5px" }}>
                                    Add Driver Call
                                </span>
                            </Space>
                        }
                        variant="borderless"
                        styles={{
                            header: {
                                background: PURPLE,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                borderBottom: "none",
                                padding: "16px 24px",
                            },
                            body: { padding: "24px 32px", background: "#fff", borderRadius: "0 0 16px 16px" },
                        }}
                        className="fit-scroll-body"
                        style={{ ...fitCardStyle, borderRadius: 16, border: "2px solid #86398f70", boxShadow: "0 10px 40px rgba(137, 67, 154, 0.08)" }}
                    >
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={onFinish}
                            size="large"
                            requiredMark={false}
                            initialValues={{ type: "incoming" }}
                        >
                            <Form.Item
                                label={
                                    <span style={{ fontWeight: 600, color: "#444" }}>
                                        Cab ID
                                    </span>
                                }
                                name="cabId"
                                rules={[
                                    {
                                        required: true,
                                        message: "Please enter Cab ID",
                                    },
                                    {
                                        pattern: /^[A-Za-z0-9]+$/,
                                        message: "Only letters and numbers are allowed",
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="E.g., CAB001"
                                    autoComplete="off"
                                    maxLength={20}
                                    style={{
                                        borderRadius: 8,
                                        height: 46,
                                    }}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(
                                            /[^A-Za-z0-9]/g,
                                            ""
                                        );

                                        form.setFieldsValue({
                                            cabId: value,
                                        });
                                    }}
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span style={{ fontWeight: 600, color: "#444" }}>Call Type</span>}
                                name="type"
                                rules={[{ required: true, message: "Please select call type" }]}
                            >
                                <Select placeholder="Select type" style={{ borderRadius: 8, height: 46 }}>
                                    <Option value="incoming">Incoming 📥</Option>
                                    <Option value="outgoing">Outgoing 📤</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={<span style={{ fontWeight: 600, color: "#444" }}>Reason</span>}
                                name="reason"
                                rules={[{ required: true, message: "Please select a reason" }]}
                            >
                                <Select placeholder="Select reason" showSearch style={{ borderRadius: 8, height: 46 }}>
                                    {REASONS.map((r) => (
                                        <Option key={r} value={r}>{r}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label={<span style={{ fontWeight: 600, color: "#444" }}>Remarks</span>}
                                name="remarks"
                            >
                                <TextArea
                                    rows={4}
                                    maxLength={300}
                                    showCount
                                    placeholder="Enter any specific remarks..."
                                    style={{ borderRadius: 8, resize: "none" }}
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0, marginTop: 32 }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SendOutlined />}
                                    loading={adding}
                                    block
                                    size="large"
                                    style={{
                                        background: PURPLE,
                                        border: "none",
                                        borderRadius: 12,
                                        fontWeight: 700,
                                        fontSize: 16,
                                        letterSpacing: "0.5px",
                                        boxShadow: "0 6px 16px rgba(137, 67, 154, 0.35)",
                                        height: 52,
                                    }}
                                >
                                    Save Call Log
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </div>

                <div style={tableColStyle}>
                    <Card
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", color: "#fff", flexWrap: "wrap", gap: 12 }}>
                                <Space size={10}>
                                    <HistoryOutlined style={{ fontSize: 20 }} />
                                    <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "0.5px" }}>
                                        Call History
                                    </span>
                                </Space>

                                {/* <Space size={8} wrap>
                                    <Tag style={{ borderRadius: 12, fontWeight: 600, padding: "2px 10px" }}>
                                        Total: {rows.length}
                                    </Tag>
                                    <Tag color="blue" style={{ borderRadius: 12, fontWeight: 600, padding: "2px 10px" }}>
                                        Incoming: {incomingCount}
                                    </Tag>
                                    <Tag color="green" style={{ borderRadius: 12, fontWeight: 600, padding: "2px 10px" }}>
                                        Outgoing: {outgoingCount}
                                    </Tag>
                                </Space> */}
                            </div>
                        }
                        extra={null}
                        variant="borderless"
                        styles={{
                            header: {
                                background: PURPLE,
                                borderTopLeftRadius: 16,
                                borderTopRightRadius: 16,
                                borderBottom: "1px solid rgba(137, 67, 154, 0.1)",
                                padding: "16px 24px",
                            },
                            body: { ...historyBodyStyle, background: "#fff", border: "2px solid #86398f70", borderRadius: "0 0 16px 16px" },
                        }}
                        className="fit-card"
                        style={{ ...fitCardStyle, borderRadius: 16, boxShadow: "0 10px 40px rgba(137, 67, 154, 0.08)" }}
                    >
                        <div className="fit-table">
                            <Table
                                className="custom-black-grid"
                                columns={historyColumns}
                                dataSource={rows}
                                rowKey={(record: any) => record.id}
                                scroll={{ x: 1000, y: 240 }}
                                locale={{
                                    emptyText: (
                                        <Empty
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            description={<Text type="secondary">No calls found for this range.</Text>}
                                        />
                                    ),
                                }}
                                pagination={{
                                    pageSize: 10,
                                    pageSizeOptions: ["5", "10", "50", "100"],
                                    showSizeChanger: true,
                                    showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} records`,
                                }}
                                style={{ marginBottom: 0 }}
                                rowHoverable
                            />
                        </div>
                    </Card>
                </div>
            </div>

            <style>{`
                .drv-layout {
                    display: flex;
                    gap: 24px;
                    flex: 1 1 auto;
                    min-height: 0;
                    /* stretch, not flex-start: the columns have to inherit the
                       row's height for the cards to size against it. */
                    align-items: stretch;
                }

                .custom-black-grid .ant-table-container {
                    border-top: 1px solid #d9d9d9 !important;
                    border-left: 1px solid #d9d9d9 !important;
                    border-radius: 0 !important;
                }
                .custom-black-grid .ant-table-thead > tr > th,
                .custom-black-grid .ant-table-tbody > tr > td {
                    border-bottom: 1px solid #d9d9d9 !important;
                    border-right: 1px solid #d9d9d9 !important;
                    border-radius: 0 !important;
                }
                .custom-black-grid .ant-table-thead > tr > th {
                    background: #86398f !important;
                    color: #fff !important;
                }
                .custom-black-grid .ant-table-thead > tr > th::before {
                    display: none !important;
                }
                .custom-black-grid .ant-table-column-sorter,
                .custom-black-grid .ant-table-column-sorter-up,
                .custom-black-grid .ant-table-column-sorter-down {
                    color: rgba(255,255,255,0.55) !important;
                }
                .custom-black-grid .ant-table-column-sorter-up.active,
                .custom-black-grid .ant-table-column-sorter-down.active {
                    color: #ffd666 !important;
                }
                .custom-black-grid .ant-table-tbody > tr:nth-child(even) > td {
                    background: #faf7fb;
                }
                .custom-black-grid .ant-table-tbody > tr:hover > td {
                    background: #f3e8f6 !important;
                }

                /* This grid owns the only horizontal scroll on the page, so it
                   needs a bar the user can see - .fit-table hides the ones on
                   a table that is merely a fitted viewport (see
                   style/fit-table.css), hence the !important. */
                .custom-black-grid .ant-table-body,
                .custom-black-grid .ant-table-content {
                    scrollbar-width: thin !important;
                    scrollbar-color: rgba(136, 57, 143, 0.35) transparent !important;
                }
                .custom-black-grid .ant-table-body::-webkit-scrollbar,
                .custom-black-grid .ant-table-content::-webkit-scrollbar {
                    width: 6px !important;
                    height: 6px !important;
                    display: block !important;
                }
                .custom-black-grid .ant-table-body::-webkit-scrollbar-thumb,
                .custom-black-grid .ant-table-content::-webkit-scrollbar-thumb {
                    background: rgba(136, 57, 143, 0.35) !important;
                    border-radius: 6px !important;
                }
            `}</style>
        </div>
    );
};

export default AddDrivers;